import React from "react";

import useEmblaCarousel from "embla-carousel-react";
import { useNavigate } from "react-router-dom";

import useFavoritesCardMenu from "../hooks/useFavoritesCardMenu";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";

import FavoritesContextMenu from "./FavoritesContextMenu";

const SeriesCarousel = ({
  items = [],
  isDarkMode = true,
  itemWidth = null,
  enableContextMenu = false,
  seeAllHref = null,
}) => {
  const navigate = useNavigate();
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: false,
    dragFree: true,
    skipSnaps: false,
    watchDrag: true,
    watchSlides: false, // Prevent recalculation on slide changes
  });

  const goToDetail = (item) => {
    const mediaId =
      item.mediaId && item.mediaId !== -1
        ? item.mediaId
        : item.tmdbId || item.id;
    const mediaType = item.mediaType || "series";

    if (mediaId && mediaId !== -1) {
      if (mediaType === "movie") {
        navigate(`/web-player/movie/${mediaId}`);
      } else {
        navigate(`/web-player/series/${mediaId}`);
      }
    }
  };

  const menu = useFavoritesCardMenu({
    enabled: enableContextMenu,
    mediaTypeOf: (it) => it.mediaType || "series",
    goToDetail,
  });

  if (!items || items.length === 0) return null;

  const renderImage = (item) =>
    item.posterPath ||
    item.poster ||
    item.stream_icon ||
    item.logo ||
    item.thumbnail;

  return (
    <>
      <div
        className="home-edge-row select-none"
        ref={emblaRef}
        style={{
          paddingTop: 10,
          paddingBottom: 10,
          overflowX: "clip",
          overflowY: "visible",
          width: "100%",
        }}
      >
        <div className="flex gap-4" style={{ overflow: "visible" }}>
          {items.map((item, idx) => {
            const key =
              item.id ?? item.stream_id ?? item.title ?? item.name ?? idx;
            const image = renderImage(item);
            const isLastItem = idx === items.length - 1 && !seeAllHref;

            // Mobile: 2 items per row (calc half screen minus padding and gap)
            // Mobile Landscape: 4.5 items per row
            // Tablet/Desktop: progressively larger
            const widthClass = itemWidth
              ? ""
              : "w-[calc((100vw-48px)/2)] sm:w-[192px] md:w-[216px] lg:w-[240px] max-[932px]:landscape:w-[calc((100vw-96px)/4.5)]";

            return (
              <div
                className={`series-fixed-item flex-shrink-0 ${widthClass} ${isLastItem ? "mr-32" : ""} cursor-pointer`}
                key={key}
                role="button"
                style={itemWidth ? { width: itemWidth } : undefined}
                tabIndex={0}
                onClick={() => menu.handleCardClick(item)}
                onKeyDown={activateOnEnterOrSpace}
                onMouseDown={() => menu.startPress(item)}
                onMouseLeave={menu.endPress}
                onMouseUp={menu.endPress}
                onTouchEnd={menu.endPress}
                onTouchStart={() => menu.startPress(item)}
              >
                <article className="transform-gpu">
                  <div className="relative group">
                    <div
                      className="lunova-card relative card-radius-poster"
                      style={{
                        aspectRatio: "2 / 3",
                        transformOrigin: "center center",
                        boxShadow:
                          "0 15px 35px -10px rgba(0,0,0,0.75), " +
                          "0 10px 20px -5px rgba(0,0,0,0.5)",
                      }}
                    >
                      <div className="lunova-card-clip">
                        {image ? (
                          <img
                            alt={item.name || item.title || "Content"}
                            className="w-full h-full object-cover"
                            draggable={false}
                            src={image}
                            onDragStart={(e) => e.preventDefault()}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.parentElement.innerHTML = `<div class="flex items-center justify-center text-white text-lg font-bold p-4 text-center h-full" style="background: linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)">${
                                item.name || item.title || "Content"
                              }</div>`;
                            }}
                          />
                        ) : (
                          <div
                            className="flex items-center justify-center text-white text-lg font-bold p-4 text-center h-full"
                            style={{
                              background:
                                "linear-gradient(135deg, rgba(30,30,40,0.9) 0%, rgba(50,50,65,0.9) 100%)",
                            }}
                          >
                            {item.name || item.title || "Content"}
                          </div>
                        )}

                        {/* Progress Bar - Show if item has progress */}
                        {item.progress > 0 && (
                          <div className="absolute bottom-3 left-0 right-0 flex justify-center px-4 z-10">
                            <div
                              className="h-2 bg-gray-600 rounded-full overflow-hidden"
                              style={{ width: "80%" }}
                            >
                              <div
                                className="h-full rounded-full transition-[width] duration-300"
                                style={{
                                  backgroundColor: "#6495ED",
                                  width: `${Math.min(Math.max(0, item.progress || 0), 100)}%`,
                                }}
                                title={`Progress: ${Math.round(item.progress || 0)}%`}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Rotten Tomatoes Ratings - Below the portrait */}
                    {item.rottenTomatoes && (
                      <div className="flex justify-center mt-2 select-none gap-3">
                        {/* Critics Score */}
                        {item.rottenTomatoes.critics?.score &&
                          item.rottenTomatoes.critics.score !== "0" && (
                            <div className="flex items-center gap-1">
                              {item.rottenTomatoes.critics.icon ? (
                                <img
                                  alt="Critics rating"
                                  className="w-4 h-4 object-contain"
                                  src={item.rottenTomatoes.critics.icon}
                                />
                              ) : (
                                <span className="text-sm">🍅</span>
                              )}
                              <span
                                className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}
                              >
                                {item.rottenTomatoes.critics.score}%
                              </span>
                            </div>
                          )}
                        {/* Audience Score */}
                        {item.rottenTomatoes.audience?.score &&
                          item.rottenTomatoes.audience.score !== "0" && (
                            <div className="flex items-center gap-1">
                              {item.rottenTomatoes.audience.icon ? (
                                <img
                                  alt="Audience rating"
                                  className="w-4 h-4 object-contain"
                                  src={item.rottenTomatoes.audience.icon}
                                />
                              ) : (
                                <span className="text-sm">🍿</span>
                              )}
                              <span
                                className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}
                              >
                                {item.rottenTomatoes.audience.score}%
                              </span>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </article>
              </div>
            );
          })}
          {seeAllHref && items.length > 0 && (
            <button
              className="series-fixed-item flex-shrink-0 w-[calc((100vw-48px)/2)] sm:w-[192px] md:w-[216px] lg:w-[240px] mr-32 cursor-pointer"
              type="button"
              onClick={() => navigate(seeAllHref)}
            >
              <div
                className="flex items-center justify-center rounded-xl bg-white/10 text-white text-lg font-semibold"
                style={{ aspectRatio: "2 / 3" }}
              >
                See All
              </div>
            </button>
          )}
        </div>
      </div>

      {menu.contextMenuItem && (
        <FavoritesContextMenu
          actions={[
            {
              key: "play",
              icon: "fa-play",
              label: "Play Now",
              onClick: () => goToDetail(menu.contextMenuItem),
            },
            {
              key: "watched",
              icon: "fa-circle-check",
              label: "Mark as Watched",
              onClick: () => menu.markAsWatched(menu.contextMenuItem),
            },
            {
              key: "fav",
              icon: "fa-bookmark",
              label: menu.isFav(menu.contextMenuItem)
                ? "Clear from Favorites"
                : "Add to Favorites",
              onClick: () => menu.toggleFavorite(menu.contextMenuItem),
            },
            {
              key: "details",
              icon: "fa-circle-info",
              label: "View Details",
              onClick: () => goToDetail(menu.contextMenuItem),
            },
          ]}
          item={menu.contextMenuItem}
          type="series"
          onClose={() => menu.setContextMenuItem(null)}
        />
      )}
    </>
  );
};

export default SeriesCarousel;
